/* Name-based compatibility, Pythagorean numerology.
 *
 * Deterministic by design: the same two names always return the same score, in
 * either order. A calculator that returned a different number on every reload
 * would be obvious nonsense to anyone who tried it twice, and Astrotalk's does
 * hold steady, so ours has to as well.
 *
 * No API call. This is arithmetic on the letters.
 */

/** Pythagorean table: A=1 … I=9, J=1 … R=9, S=1 … Z=8. */
function letterValue(ch: string): number {
  const code = ch.charCodeAt(0) - 96; // 'a' -> 1
  if (code < 1 || code > 26) return 0;
  return ((code - 1) % 9) + 1;
}

const clean = (name: string) => name.toLowerCase().replace(/[^a-z]/g, "");

/** Sum of every letter, un-reduced. Used for the fine-grain spread. */
function letterSum(name: string): number {
  return clean(name)
    .split("")
    .reduce((total, ch) => total + letterValue(ch), 0);
}

/** Reduce to a root 1-9, preserving master numbers 11, 22 and 33 as themselves
 *  before the final fold, which is how numerology treats them. */
export function nameNumber(name: string): number {
  let n = letterSum(name);
  while (n > 9 && n !== 11 && n !== 22 && n !== 33) {
    n = String(n)
      .split("")
      .reduce((t, d) => t + Number(d), 0);
  }
  return n > 9 ? String(n).split("").reduce((t, d) => t + Number(d), 0) : n;
}

/* Root-to-root affinity, 1-9. Symmetric by construction: the grid is mirrored,
   so Priya + Rahul scores the same as Rahul + Priya. Values follow the standard
   numerology groupings, where 1/5/7 are independent, 2/6/9 are relational and
   4/8 are grounded. */
const AFFINITY: number[][] = [
  /*        1   2   3   4   5   6   7   8   9 */
  /* 1 */ [78, 62, 80, 66, 88, 70, 72, 64, 74],
  /* 2 */ [62, 84, 68, 82, 60, 92, 66, 76, 86],
  /* 3 */ [80, 68, 82, 58, 84, 88, 64, 62, 90],
  /* 4 */ [66, 82, 58, 80, 62, 78, 74, 90, 68],
  /* 5 */ [88, 60, 84, 62, 76, 66, 86, 64, 70],
  /* 6 */ [70, 92, 88, 78, 66, 86, 68, 74, 94],
  /* 7 */ [72, 66, 64, 74, 86, 68, 78, 60, 72],
  /* 8 */ [64, 76, 62, 90, 64, 74, 60, 82, 66],
  /* 9 */ [74, 86, 90, 68, 70, 94, 72, 66, 88],
];

export interface LoveResult {
  percent: number;
  yourNumber: number;
  partnerNumber: number;
  band: "strong" | "excellent" | "understanding" | "differences";
  headline: string;
  detail: string;
}

const BANDS = {
  strong: {
    headline: "Strong connection",
    detail:
      "The numbers line up well here. Talking probably comes easily and you are comfortable around each other quickly. It still takes work, like anything does, it just starts from a better place than most.",
  },
  excellent: {
    headline: "Good match, with room in it",
    detail:
      "You fit together without turning into the same person. The differences that turn up here are not problems to fix, they are usually the bits you end up learning from.",
  },
  understanding: {
    headline: "Different, and that is workable",
    detail:
      "Your numbers pull in different directions. That is not a warning. Plenty of long relationships are built between people who see things differently, as long as both of you keep talking and leave each other some room.",
  },
  differences: {
    headline: "More contrast than overlap",
    detail:
      "Not many shared threads in the numbers. It says nothing about loyalty, kindness or wanting the same things, none of which a name can measure. Read it as a reason to look closer, not a verdict.",
  },
} as const;

export function loveScore(yourName: string, partnerName: string): LoveResult | null {
  const a = clean(yourName);
  const b = clean(partnerName);
  if (a.length < 2 || b.length < 2) return null;

  const yourNumber = nameNumber(a);
  const partnerNumber = nameNumber(b);
  const base = AFFINITY[yourNumber - 1][partnerNumber - 1];

  /* Two names can share a root and still be different names, so a symmetric
     spread from the raw letter sums separates them by a few points. Sorted
     before combining so the pair order cannot change the result. */
  const [lo, hi] = [letterSum(a), letterSum(b)].sort((x, y) => x - y);
  const spread = ((lo * 31 + hi * 17) % 11) - 5; // -5 … +5

  const percent = Math.max(38, Math.min(99, base + spread));

  const band: LoveResult["band"] =
    percent >= 85
      ? "strong"
      : percent >= 70
        ? "excellent"
        : percent >= 55
          ? "understanding"
          : "differences";

  return { percent, yourNumber, partnerNumber, band, ...BANDS[band] };
}
